import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { useGoogleIdentity, GOOGLE_CLIENT_ID } from "../../lib/google/gapi"
import { isRunningInBrowser } from "../../lib/browser"
import { createClient } from "@/lib/supabase/client"
import { Provider } from "@supabase/supabase-js"

interface SigninButtonProps {
  className?: string
  size?: 'large' | 'medium' | 'small'
  onSuccess?: (credential: string) => void
  onError?: (error: Error) => void
}

export function PureGoogleSigninButton({
  className = '',
  size = 'large',
  onSuccess,
  onError
}: SigninButtonProps) {
  const { googleIdentity } = useGoogleIdentity()
  const buttonId = 'google-signin-upgrade-modal'
  
  useEffect(() => {
    if (Boolean(googleIdentity) && isRunningInBrowser()) {
      const parent = document.getElementById(buttonId)
      if (!parent) return
      
      googleIdentity!.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: any) => {
          if (response.credential) {
            const supabase = createClient()
            try {
              const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                  queryParams: {
                    access_type: 'offline',
                    prompt: 'consent',
                  },
                  skipBrowserRedirect: true
                }
              })
              if (error) throw error
              onSuccess?.(response.credential)
            } catch (err) {
              onError?.(err instanceof Error ? err : new Error('Failed to sign in with Google'))
            }
          }
        },
        error_callback: (error: Error) => {
          onError?.(error)
        }
      })
      
      googleIdentity!.accounts.id.renderButton(parent!, {
        theme: "filled_blue",
        size: size === 'large' ? 'large' : 'medium',
        text: 'continue_with',
        width: parent.offsetWidth
      })
    }
  }, [googleIdentity, size, onSuccess, onError])

  return (
    <div className={`${className} max-w-full overflow-hidden my-auto`}>
      <div id={buttonId} />
    </div>
  )
}

function GoogleIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

export function PureMicrosoftSigninButton({ 
  className = '', 
  onSuccess,
  onError
}: SigninButtonProps) {
  const [loading, setLoading] = useState(false)
  const supabase = createClient()

  const handleClick = async () => {
    setLoading(true)
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          scopes: 'email',
          skipBrowserRedirect: true
        }
      })
      
      if (error) throw error
      
      // Force revalidate the session
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        onSuccess?.('microsoft')
      }
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('Failed to sign in with Microsoft'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      variant="outline"
      onClick={handleClick}
      className={`w-full ${className}`}
      disabled={loading}
    >
      <MicrosoftIcon className="w-5 h-5 mr-2" />
      <span className="font-medium">
        {loading ? 'Signing in...' : 'Continue with Microsoft'}
      </span>
    </Button>
  )
}

function MicrosoftIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
      <title>Microsoft</title>
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  )
}

interface PureSigninButtonsProps {
  providers: Provider[]
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export function PureSigninButtons({ providers, onSuccess, onError }: PureSigninButtonsProps) {
  const supabase = createClient()

  const handleSignIn = async (provider: Provider) => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (error) throw error
      if (onSuccess) onSuccess()
    } catch (error) {
      if (onError) onError(error as Error)
    }
  }

  return (
    <div className="space-y-2">
      {providers.map((provider) => (
        <Button
          key={provider}
          variant="outline"
          className="w-full"
          onClick={() => handleSignIn(provider)}
        >
          Sign in with {provider}
        </Button>
      ))}
    </div>
  )
} 